import * as React from "react";
import { PropsWithChildren } from "react";
import * as ReactDOM from "react-dom";

import { ICandyComponentHandle } from "../../../Engine/Core/ComponentHandleRegistry/ICandyComponentHandle";
import { createHocDisplayName, emptyRef, GetProps } from "../../TypingUtils";
import { normalizeComponentIdExtractor, OptionalComponentIdExtractor } from "../ComponentIdExtraction";
import { useComponentHandleRegistry } from "../../../Engine/Core/ComponentHandleRegistry/ComponentHandleRegistryContext";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { DomElementHack } from "../../Commons/DOM/DomElementHack";

import { FocusRequestsSourceContext, IFocusRequestsSource } from "./FocusRequestsSources";

const exclusiveFocusByPath = "exclusiveFocusByPath";

export function withFocusManagement<C extends React.ComponentType<GetProps<C>>>(
    WrappedComponent: C,
    inExtractComponentIds?: OptionalComponentIdExtractor<
        Readonly<JSX.LibraryManagedAttributes<C, PropsWithChildren<GetProps<C>>>>
    >
): React.ComponentType<JSX.LibraryManagedAttributes<C, PropsWithChildren<GetProps<C>>>> {
    const extractComponentIds = normalizeComponentIdExtractor(inExtractComponentIds);

    function WithFocusManagementWrapper(
        props: JSX.IntrinsicAttributes & JSX.LibraryManagedAttributes<C, PropsWithChildren<GetProps<C>>>
    ): JSX.Element {
        const componentHandleRegistry = useComponentHandleRegistry();

        const domElementHackRef = React.useRef<DomElementHack | null>(emptyRef);

        const focusRef = React.useRef<(path?: unknown) => void>();
        const makePathAvailableForFocusRef = React.useRef<(path: AbsoluteModelPath) => Promise<void>>();

        const exclusiveRegistration = exclusiveFocusByPath in props && props[exclusiveFocusByPath] === true;

        const focusManager = React.useRef<IFocusRequestsSource>({
            addRequestFocusListener: (onFocus: (path?: unknown) => void): void => {
                focusRef.current = onFocus;
            },
            removeRequestFocusListener: (): void => {
                focusRef.current = undefined;
            },
            addRequestPathAvailabilityListener: (listener: (path: AbsoluteModelPath) => Promise<void>): void => {
                makePathAvailableForFocusRef.current = listener;
            },
            removeRequestPathAvailabilityListener: (): void => {
                makePathAvailableForFocusRef.current = undefined;
            },
        });

        const componentHandle = React.useRef<ICandyComponentHandle>({
            focus: (path?: unknown) => {
                focusRef.current?.(path);
            },
            makePathAvailableForFocus: async (path: AbsoluteModelPath) => {
                await makePathAvailableForFocusRef.current?.(path);
            },
            getAnchorElement: (): HTMLElement | undefined => {
                if (domElementHackRef.current == undefined) {
                    return undefined;
                }
                const result = ReactDOM.findDOMNode(domElementHackRef.current);
                if (result != undefined && result instanceof HTMLElement) {
                    return result;
                }
                return undefined;
            },
            getTargetBoundingClientRect: (): DOMRect | undefined => {
                if (domElementHackRef.current == undefined) {
                    return undefined;
                }
                const result = ReactDOM.findDOMNode(domElementHackRef.current);
                if (result != undefined && result instanceof HTMLElement) {
                    return result.getBoundingClientRect();
                }
                return undefined;
            },
        });

        React.useEffect(() => {
            const controlIds = [...extractComponentIds(props)];
            for (const id of controlIds) {
                componentHandleRegistry.registerComponentHandle(id, componentHandle.current, exclusiveRegistration);
            }
            return () => {
                for (const id of controlIds) {
                    componentHandleRegistry.unregisterComponentHandle(id, componentHandle.current);
                }
            };
        }, [...extractComponentIds(props)]);

        return (
            <FocusRequestsSourceContext.Provider value={focusManager.current}>
                <DomElementHack ref={domElementHackRef}>
                    <WrappedComponent {...props} />
                </DomElementHack>
            </FocusRequestsSourceContext.Provider>
        );
    }
    WithFocusManagementWrapper.displayName = createHocDisplayName(WrappedComponent, "FocusManagement");

    return WithFocusManagementWrapper;
}
